'use client';

import useSmoothScrollingContext from '@/components/providers/context/smooth-scrolling-context';
import clsx from 'clsx';
import Link from 'next/link';
import { type Dispatch, type SetStateAction, useCallback, useRef } from 'react';
import { useLocale } from '@/components/providers/context/locale-context';
import { getLocalizedHref } from '@/app/lib/utils/localized-href';

interface MenuItem {
    title: string;
    href: string;
    index: number;
    isScrollTo?: boolean;
}

interface ItemProps {
    href: string;
    title: string;
    selected: boolean;
    isScrollTo?: boolean;
    hidePopover: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

function Item({
    href,
    title,
    selected,
    isScrollTo,
    hidePopover,
    onMouseEnter,
    onMouseLeave,
}: Readonly<ItemProps>) {
    const locale = useLocale();
    const { scrollTo } = useSmoothScrollingContext();
    const localizedHref = getLocalizedHref(href, locale);

    const handleClick = useCallback(() => {
        hidePopover();
        if (isScrollTo) {
            scrollTo(href);
        }
    }, [hidePopover, isScrollTo, href, scrollTo]);

    const handleMouseEnter = useCallback(() => {
        onMouseEnter();
    }, [onMouseEnter]);

    const content = (
        <>
            <div className="h-[19px] w-[19px]">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    className={clsx('transition duration-200', {
                        'translate-x-[6px] opacity-100': selected,
                        '-translate-x-[22px] opacity-0': !selected,
                    })}
                >
                    <path
                        d="M14.0498 8.76074L10.0342 4.74512L11.0791 3.69922L16.3574 8.97754C16.6456 9.26603 16.6456 9.73399 16.3574 10.0225L11.0791 15.3008L10.0342 14.2549L14.0498 10.2393L3.16699 10.2393V8.76074L14.0498 8.76074Z"
                        fill="white"
                    />
                </svg>
            </div>
            <div
                className={clsx('text-white transition duration-200', {
                    'translate-x-[6px] text-[#6C49FF] underline underline-offset-4':
                        selected,
                    '-translate-x-[22px]': !selected,
                })}
            >
                {title}
            </div>
        </>
    );

    if (!isScrollTo) {
        return (
            <Link
                href={localizedHref}
                prefetch
                className="flex flex-row items-center gap-[4px] whitespace-nowrap py-[6px] hover:cursor-pointer"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={hidePopover}
            >
                {content}
            </Link>
        );
    }

    return (
        <button
            className="flex flex-row items-center gap-[4px] whitespace-nowrap py-[6px] hover:cursor-pointer"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={handleClick}
        >
            {content}
        </button>
    );
}

interface SubMenuListProps {
    menus: MenuItem[];
    selectedIndex: number;
    setSelectedIndex: Dispatch<SetStateAction<number>>;
    hidePopover: () => void;
}

function SubMenuList({
    selectedIndex,
    setSelectedIndex,
    menus,
    hidePopover,
}: Readonly<SubMenuListProps>) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const frameRef = useRef<number | null>(null);

    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const clearFrame = () => {
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
    };

    const handleMouseEnter = useCallback(
        (index: number) => {
            clearTimer();
            clearFrame();
            frameRef.current = requestAnimationFrame(() => {
                setSelectedIndex(index);
                frameRef.current = null;
            });
        },
        [setSelectedIndex],
    );

    const handleMouseLeave = useCallback(() => {
        clearFrame();
        clearTimer();
        timerRef.current = setTimeout(() => {
            setSelectedIndex(-1);
            timerRef.current = null;
        }, 48);
    }, [setSelectedIndex]);

    return (
        <div className="j-l1 flex flex-col">
            {menus.map((menu) => (
                <Item
                    key={menu.index}
                    hidePopover={hidePopover}
                    href={menu.href}
                    title={menu.title}
                    isScrollTo={menu.isScrollTo}
                    selected={selectedIndex === menu.index}
                    onMouseEnter={() => handleMouseEnter(menu.index)}
                    onMouseLeave={handleMouseLeave}
                />
            ))}
        </div>
    );
}

export default SubMenuList;
